import { NextSeo } from "next-seo";
import { Navbar } from "~/components/Navbar/component";
import { UserFetcher } from "~/modules/user/components/UserFetcher";
import { PageTrack } from "~/modules/analytics/components/PageTrack";
import { GetStaticPaths, GetStaticProps } from "next";
import { getPlatformURL, Routes } from "~/routes";
import { PageWrapper, SidebarLayout } from "~/components/SidebarLayout";
import { Footer } from "~/components/Footer";
import { Breadcrumbs } from "~/components/Breadcrumbs";
import {
    NEXTJS_MISS_REVALIDATE_TIMEOUT,
    NEXTJS_REVALIDATE_TIMEOUT,
} from "~/server/consts";
import { BreadcrumbSEO } from "~/modules/app/seo/BreadcrumbSEO";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import {
    getPlatformPageDescription,
    PlatformIndividualSEO,
} from "~/modules/app/seo/PlatformIndividualSEO";
import { PlatformIndividualPage } from "~/modules/platforms/pages/individual";
import { SOCIAL_OG_IMAGE_URL } from "~/consts";
import {
    getPlatformSlugsForSSG,
    getPlatformPageData,
    PlatformPageData,
} from "~/modules/platforms/api/ssg";

// // // //

export const getStaticPaths: GetStaticPaths = async () => {
    const slugs = await getPlatformSlugsForSSG();

    const paths = slugs.map((slug) => ({
        params: { platformSlug: slug },
    }));

    return {
        paths,
        fallback: "blocking",
    };
};

// // // //

export const getStaticProps: GetStaticProps<PlatformPageData> = async (ctx) => {
    const platformSlug = ctx.params?.platformSlug as string;

    if (!platformSlug) {
        throw new Error("Platform slug is missing from params");
    }

    // Fetch all platform page data with retry logic.
    // Downstream fetch failures throw (failing the build, or preserving the
    // stale page during ISR); only a genuine "platform not found" resolves
    // to null and is cached as a short-lived miss below.
    const pageData = await getPlatformPageData(platformSlug);

    if (pageData === null) {
        console.warn(`Platform not found for slug "${platformSlug}".`);
        return {
            notFound: true,
            revalidate: NEXTJS_MISS_REVALIDATE_TIMEOUT,
        };
    }

    return {
        props: pageData,
        revalidate: NEXTJS_REVALIDATE_TIMEOUT,
    };
};

// // // //

export default function PlatformPage(props: PlatformPageData) {
    const { platform, assetFilterMetadata, leagueTableTabs, aggregates } =
        props;

    const pageUrl = addBaseUrl(getPlatformURL({ platformSlug: platform.slug }));
    const description = getPlatformPageDescription({ platform });

    return (
        <PageTrack>
            <NextSeo
                title={platform.name}
                description={description}
                canonical={pageUrl}
                openGraph={{
                    type: "website",
                    url: pageUrl,
                    title: platform.name,
                    site_name: "RWA.xyz",
                    description,
                    images: [
                        {
                            url: SOCIAL_OG_IMAGE_URL,
                            width: 4000,
                            height: 2300,
                            alt: "rwa.xyz",
                        },
                    ],
                }}
                additionalMetaTags={[
                    {
                        name: "keywords",
                        content: [
                            platform.name,
                            "tokenization platform",
                            "RWA platform",
                            "real world assets",
                            "asset tokenization",
                        ].join(", "),
                    },
                ]}
            />
            <PlatformIndividualSEO platform={platform} />
            <BreadcrumbSEO
                items={[
                    {
                        name: "Tokenization Platforms",
                        href: Routes.platforms,
                    },
                    {
                        name: platform.name,
                        href: getPlatformURL({ platformSlug: platform.slug }),
                    },
                ]}
            />
            <UserFetcher>{({ user }) => <Navbar user={user} />}</UserFetcher>
            <SidebarLayout pathname={Routes.platformIndividual}>
                <Breadcrumbs
                    pages={[
                        {
                            name: "Tokenization Platforms",
                            href: Routes.platforms,
                            current: false,
                        },
                        {
                            name: platform.name,
                            current: true,
                        },
                    ]}
                />
                <PageWrapper>
                    <PlatformIndividualPage
                        platform={platform}
                        assetFilterMetadata={assetFilterMetadata}
                        leagueTableTabs={leagueTableTabs}
                        aggregates={aggregates}
                    />
                    <Footer />
                </PageWrapper>
            </SidebarLayout>
        </PageTrack>
    );
}
